import { Skeleton, Stack, Typography } from "@mui/material";
import { useBankingAccounts } from "Api/Queries/Accounts/useBankingAccounts";
import { AccountOrderEditor } from "Components/BankAccounts/BankAccountList/AccountOrderEditor/AccountOrderEditor";
import { BankAccountListPageContent } from "Components/BankAccounts/BankAccountList/BankAccountListPageContent";
import { HunterBankAccountListPageContent } from "Components/BankAccounts/BankAccountList/HunterBankAccountListPageContent";
import { BankAccountRowSkeleton } from "Components/BankAccounts/BankAccountList/BankAccountRow/BankAccountRowSkeleton";
import { AuthenticatedLayout } from "Components/Layout/AuthenticatedLayout";
import { PrimaryPaper } from "Components/Shared/PrimaryPaper";
import { StyledPageWrapper } from "Components/Shared/StyledComponents";
import { useIsCurrentUserHunter } from "Hooks/useIsCurrentUserHunter";
import { useState, type ReactNode } from "react";
import { Resources, useResource } from "Translations/Resources";

const PageResources = Resources.Pages.Content.BankAccounts;

export const BankAccountListPage = () => {
  const [isEditing, setIsEditing] = useState(false);
  const { data, isLoading } = useBankingAccounts();
  const isHunter = useIsCurrentUserHunter();
  const { t } = useResource();

  if (isLoading) {
    return (
      <Layout>
        <BankAccountRowSkeleton />
        <BankAccountRowSkeleton />
        <BankAccountRowSkeleton />
        <BankAccountRowSkeleton />
        <Skeleton variant="rounded" height="60px" />
      </Layout>
    );
  }

  if (!data?.items) {
    return <Layout>{t(Resources.Pages.Content.BankAccounts.Error)}</Layout>;
  }

  if (isEditing) {
    return (
      <Layout isEditing>
        <Typography fontSize={16}>
          {t(PageResources.EditAccountOrderDescription)}
        </Typography>
        <AccountOrderEditor
          initialItems={data.items}
          onSave={() => setIsEditing(false)}
        />
      </Layout>
    );
  }

  const ContentComponent = isHunter
    ? HunterBankAccountListPageContent
    : BankAccountListPageContent;

  return (
    <Layout>
      <ContentComponent
        accounts={data.items}
        onSwitchToEditOrderRequested={() => setIsEditing(true)}
      />
    </Layout>
  );
};

export default BankAccountListPage;

type LayoutProps = {
  children: ReactNode;
  isEditing?: boolean;
};

const Layout = ({ children, isEditing = false }: LayoutProps) => {
  const { t } = useResource();

  return (
    <AuthenticatedLayout
      title={t(
        isEditing
          ? Resources.Pages.Titles.BankAccountOrder
          : Resources.Pages.Titles.BankAccounts,
      )}
      backNavigation={{
        mode: "historyStack",
        fallbackUrl: null, // "root" page, nowhere higher to go
      }}
    >
      <StyledPageWrapper>
        <div className="left-content">
          <PrimaryPaper>
            <Stack gap={4}>{children}</Stack>
          </PrimaryPaper>
        </div>
      </StyledPageWrapper>
    </AuthenticatedLayout>
  );
};
